import Vue from 'vue';
import Vuex, {ActionTree, GetterTree, Module, MutationTree} from 'vuex';

import FilterMapper from '../../mappings/filter-mapper';
import { IFilterService } from "../../services/interfaces/filter-service-interface";

import { IDictionary } from '@/shared/models/interface/dictionary-model';
import { IFilterItem } from '../../models/filter-item-model';
import IFilterGroup, { IFilterGroupViewModel } from "../../models/filter-group-model";

Vue.use(Vuex);

class FilterState {
    filterService!: IFilterService
    userFilters!: IFilterGroup[]
    filterGroups: IDictionary<IFilterGroupViewModel> = {}
    activeFilterGroup: string = ""
    filterItems: IDictionary<IFilterItem> = {}
    activeFilterItems: string[] = []
    mobileFilters: boolean = false;
}

const mutations: MutationTree<FilterState> = {
    setFilterService(state: FilterState, filterService: IFilterService) {
        state.filterService = filterService
    },

    setUserFilters(state: FilterState, filters: IFilterGroup[] = []) {
        state.userFilters = filters
    },

    setFilterGroups(state: FilterState, filters: IFilterGroup[] = []) {
        state.filterGroups = FilterMapper.filterGroups(filters)
    },

    setFilterItems(state: FilterState, filters: IFilterGroup[] = []) {
        state.filterItems = FilterMapper.filterItems(filters);
    },

    setActiveFilterGroup(state: FilterState, filterGroup: string) {
        state.activeFilterGroup = filterGroup;
    },

    setActiveFilter(state: FilterState, codename: string) {
        let index: number = state.activeFilterItems.indexOf(codename);

        if (index > -1) {
            Vue.delete(state.activeFilterItems, index);
        } else {
            state.activeFilterItems.push(codename);
        }
    },

    resetActiveFilters(state: FilterState) {
        state.activeFilterItems = [];
        state.activeFilterGroup = '';
    },

    disableFilterItem(state: FilterState, { codename, isDisabled }) {
        Vue.set(state.filterItems[codename], 'Disabled', isDisabled);
    },

    setMobileFiltersOpened(state: FilterState, value: boolean) {
        state.mobileFilters = value;
    },

    toggleMobileFilters(state) {
        state.mobileFilters = !state.mobileFilters;
    }
}

const actions: ActionTree<FilterState, any> = {
    handleSearchFilters({ commit, state, dispatch, rootState }, [searchFilters]) {
        state.filterService.searchFilters = searchFilters

        if (rootState.isInitialized) {
            dispatch('disableFilters', searchFilters)
        } else {
            let userFilters = state.filterService.initialize()
            commit('setFilterGroups', userFilters)
            commit('setFilterItems', userFilters)
        }
    },

    disableFilters({ commit, state, getters }) {
        getters.allFilterItems.forEach((codename: string) => {
            let filterItem: IFilterItem = getters.singleFilterItem(codename)
            let filterGroup: IFilterGroup = getters.singleFilterGroup(filterItem.FilterGroup)
            let activeFilterGroup: string = state.activeFilterGroup;

            let shouldDisable = state.filterService.disable(filterGroup, filterItem, activeFilterGroup);
            commit('disableFilterItem', { codename: codename, isDisabled: shouldDisable });
        });
    },

    removeFilter({ commit }, [codename, parent]) {
        commit('setActiveFilter', codename);
        commit('setActiveFilterGroup', parent);
    }
}

const getters: GetterTree<FilterState, any> = {
    activeFilters(state): IFilterItem[] {
        return state.activeFilterItems.map(x => state.filterItems[x]) as IFilterItem[];
    },

    groupedActiveFilters(state, getters): IDictionary<IFilterItem[]> {
        return getters.activeFilters.reduce((result: IDictionary<IFilterItem[]>, current: IFilterItem) => {
            if (!result[current.FilterGroup]) {
                result[current.FilterGroup] = [];
            }

            result[current.FilterGroup].push(current);

            return result
        }, {});
    },

    filterGroupDisplayNames(state): string[] {
        return Object.keys(state.filterGroups);
    },

    allFilterItems(state, getters): string[] {
        return getters.filterGroupDisplayNames.flatMap((x: string) => state.filterGroups[x].FilterItems)
    },

    singleFilterItem: (state) => (name: string) => {
        return Object.values(state.filterItems).find((x: IFilterItem) => x.DisplayName == name || x.FilterCodeName == name);
    },

    singleFilterGroup: (state) => (name: string) => {
        return state.filterGroups[name];
    },

    filterItemsOfGroup: (state) => (name: string) => {
        let filterItemCodenames = state.filterGroups[name].FilterItems;
        return filterItemCodenames.map((x: string) => state.filterItems[x]);
    },

    filterGroupTitle: (state) => (filterGroupName: string) => {
        let filterItems = state.filterGroups[filterGroupName].FilterItems;
        let activeFilterItems = state.activeFilterItems.filter((x: string) => filterItems.includes(x));

        return activeFilterItems.length ? `${filterGroupName} (${activeFilterItems.length})` : filterGroupName;
    }
}

export const filterStoreModule: Module<FilterState, any> = {
    namespaced: true,
    state: new FilterState,
    mutations: mutations,
    actions: actions,
    getters: getters,
}